<template>
  <div class="cps">
    <div v-if="showEmptyBanner" class="cps-banner">
      <span class="mdi mdi-information-outline"></span>
      Completa los datos de tu parque para que aparezcan correctamente en los estados de cuenta y reportes generados por el sistema.
    </div>

    <!-- Ubicación: de aquí salen la ciudad y el clima del encabezado -->
    <section class="cps-group">
      <span class="cps-group-title">Ubicación del parque</span>

      <label class="cps-label">Dirección</label>
      <input v-model="location.address" class="cps-input" placeholder="Calle, número y colonia o carretera y kilómetro" @input="onChange('ubicacion')" />

      <div class="cps-row-2col">
        <div>
          <label class="cps-label">Ciudad</label>
          <input v-model="location.city" class="cps-input" @input="onChange('ubicacion')" />
        </div>
        <div>
          <label class="cps-label">Estado</label>
          <input v-model="location.state" class="cps-input" @input="onChange('ubicacion')" />
        </div>
      </div>
      <span v-if="location.city && location.located === false" class="cps-error">No encontramos esa ciudad: revisa cómo está escrita o el estado para ver el clima.</span>
      <span v-else-if="location.located" class="cps-hint">Clima del parque activo en el encabezado.</span>
      <span v-else class="cps-hint">Con la ciudad y el estado mostramos el clima de tu parque en el encabezado.</span>

      <button class="cps-save-btn" :disabled="!dirty.ubicacion" @click="onSaveLocation">Guardar ubicación</button>
    </section>

    <!-- Grupo 1: Identidad -->
    <section class="cps-group">
      <span class="cps-group-title">Identidad del parque</span>

      <label class="cps-label">Nombre del parque</label>
      <input v-model="identity.name" class="cps-input" @input="onChange('parque')" />

      <label class="cps-label">RFC</label>
      <input v-model="identity.rfc" class="cps-input" :class="{ 'cps-input--error': identity.rfc && !rfcValid }" @input="onChange('parque')" />
      <span v-if="identity.rfc && !rfcValid" class="cps-error">RFC con formato inválido</span>

      <label class="cps-label">Dirección fiscal completa</label>
      <textarea v-model="identity.fiscalAddress" class="cps-textarea" rows="3" @input="onChange('parque')"></textarea>

      <div class="cps-row-2col">
        <div>
          <label class="cps-label">Teléfono</label>
          <input v-model="identity.phone" class="cps-input" @input="onChange('parque')" />
        </div>
        <div>
          <label class="cps-label">Correo de contacto</label>
          <input v-model="identity.contactEmail" class="cps-input" :class="{ 'cps-input--error': identity.contactEmail && !emailValid }" @input="onChange('parque')" />
        </div>
      </div>

      <label class="cps-label">Logo del parque</label>
      <div v-if="!identity.logoUrl" class="cps-logo-dropzone" @click="logoInputEl?.click()">
        <span class="mdi mdi-image-outline"></span>
        <span>Arrastra tu logo aquí o haz clic para seleccionar</span>
      </div>
      <div v-else class="cps-logo-preview-wrap">
        <img :src="logoSrc" class="cps-logo-preview" />
        <div class="cps-logo-actions">
          <button class="cps-link-btn" @click="logoInputEl?.click()">Cambiar logo</button>
          <button class="cps-link-btn cps-link-btn--red" @click="onDeleteLogo">Eliminar</button>
        </div>
      </div>
      <input ref="logoInputEl" type="file" accept="image/png,image/svg+xml" style="display: none" @change="onLogoSelected" />
      <span class="cps-hint">Aparecerá en el encabezado de estados de cuenta y reportes. Recomendado: fondo transparente, mínimo 200x200px.</span>

      <button class="cps-save-btn" :disabled="!dirty.parque" @click="onSaveIdentity">Guardar datos de identidad</button>
    </section>

    <!-- Grupo 2: Bancarios -->
    <section class="cps-group">
      <span class="cps-group-title">Datos bancarios</span>

      <label class="cps-toggle-row">
        <input type="checkbox" v-model="bank.showBankDetails" @change="onChange('cobranza-bank')" /> Mostrar datos bancarios en estados de cuenta
      </label>
      <span v-if="!bank.showBankDetails" class="cps-warn">Los estados de cuenta no incluirán instrucciones de pago.</span>

      <div class="cps-bank-fields" :class="{ 'cps-bank-fields--disabled': !bank.showBankDetails }">
        <label class="cps-label">Nombre del banco</label>
        <input v-model="bank.bankName" class="cps-input" placeholder="Ej: BBVA, Santander, Banorte" @input="onChange('cobranza-bank')" />

        <label class="cps-label">Nombre del beneficiario</label>
        <input v-model="bank.bankBeneficiary" class="cps-input" @input="onChange('cobranza-bank')" />

        <label class="cps-label">CLABE interbancaria</label>
        <input v-model="bank.bankClabe" class="cps-input" :class="{ 'cps-input--error': bank.bankClabe && !clabeValid }" maxlength="18" @input="onChange('cobranza-bank')" />
        <span v-if="bank.bankClabe && !clabeValid" class="cps-error">CLABE inválida · Verifica que los 18 dígitos sean correctos.</span>

        <label class="cps-label">Número de cuenta (opcional)</label>
        <input v-model="bank.bankAccountNumber" class="cps-input" @input="onChange('cobranza-bank')" />

        <label class="cps-label">Referencia sugerida</label>
        <input v-model="bank.bankReferenceNote" class="cps-input" placeholder="Ej: RFC del inquilino + folio del estado de cuenta" @input="onChange('cobranza-bank')" />
      </div>

      <span class="cps-label" style="margin-top: 0.6rem;">Vista previa en el estado de cuenta</span>
      <div class="cps-bank-preview">
        <template v-if="bank.showBankDetails">
          <div><strong>{{ bank.bankName || 'Banco' }}</strong> · {{ bank.bankBeneficiary || 'Beneficiario' }}</div>
          <div class="cps-bank-preview-clabe">CLABE: {{ formattedClabe || '—' }}</div>
          <div v-if="bank.bankReferenceNote" class="cps-bank-preview-note">Referencia: {{ bank.bankReferenceNote }}</div>
        </template>
        <template v-else>
          <span class="cps-hint">Sin datos bancarios visibles en el documento.</span>
        </template>
      </div>

      <button class="cps-save-btn" :disabled="!dirty['cobranza-bank']" @click="onSaveBank">Guardar datos bancarios</button>
    </section>

    <!-- Grupo 3: Físicos -->
    <section class="cps-group">
      <span class="cps-group-title">Datos físicos</span>

      <label class="cps-label">Superficie total rentable</label>
      <div class="cps-input-suffix-wrap">
        <input v-model.number="physical.totalAreaM2" type="number" class="cps-input" @input="onChange('fisico')" />
        <span class="cps-input-suffix">m²</span>
      </div>
      <span class="cps-hint-blue">Este valor se usa para calcular el costo por m² de servicios comunes. Actualízalo si cambia el área rentable total.</span>

      <label class="cps-label" style="margin-top: 0.6rem;">Número total de naves</label>
      <div class="cps-readonly-value">{{ physical.plotsCount }} (calculado automáticamente)</div>

      <label class="cps-label" style="margin-top: 0.6rem;">Zonas del parque</label>
      <div class="cps-zones">
        <div v-for="z in zones" :key="z.id" class="cps-zone-row">
          <input v-if="editingZoneId === z.id" v-model="editingZoneName" class="cps-input cps-zone-input" @keyup.enter="confirmRename(z.id)" />
          <span v-else class="cps-zone-name">{{ z.name }}</span>
          <div class="cps-zone-actions">
            <template v-if="editingZoneId === z.id">
              <button class="cps-icon-btn" @click="confirmRename(z.id)"><span class="mdi mdi-check"></span></button>
              <button class="cps-icon-btn" @click="editingZoneId = null"><span class="mdi mdi-close"></span></button>
            </template>
            <template v-else>
              <button class="cps-icon-btn" @click="startRename(z)"><span class="mdi mdi-pencil-outline"></span></button>
              <button class="cps-icon-btn" @click="onDeleteZone(z.id)"><span class="mdi mdi-close"></span></button>
            </template>
          </div>
        </div>
        <button class="cps-link-btn" @click="onAddZone">＋ Agregar zona</button>
      </div>

      <button class="cps-save-btn" :disabled="!dirty.fisico" @click="onSavePhysical">Guardar datos físicos</button>
    </section>

    <Transition name="cps-toast-fade">
      <div v-if="toastMessage" class="cps-toast"><span class="mdi mdi-check-circle"></span> {{ toastMessage }}</div>
    </Transition>
  </div>
</template>

<script setup>
import { API_ORIGIN } from '../../config'
import { ref, computed, onMounted, reactive } from 'vue'
import {
  getParkFullConfig, saveIdentity, saveLocation, saveBankDetails, savePhysical, uploadLogo, deleteLogo,
  addZone, renameZone, deleteZone,
} from '@/services/parkConfig'

const props = defineProps({ parkId: { type: String, required: true } })
const emit = defineEmits(['dirty-change'])
const identity = reactive({ name: '', rfc: '', fiscalAddress: '', phone: '', contactEmail: '', logoUrl: null })
const location = reactive({ address: '', city: '', state: '', located: null })
const bank = reactive({ showBankDetails: true, bankName: '', bankBeneficiary: '', bankClabe: '', bankAccountNumber: '', bankReferenceNote: '' })
const physical = reactive({ totalAreaM2: null, plotsCount: 0 })
const zones = ref([])

const dirty = reactive({ ubicacion: false, parque: false, 'cobranza-bank': false, fisico: false })
const toastMessage = ref(null)

async function load() {
  const config = await getParkFullConfig(props.parkId)
  if (!config) return
  Object.assign(identity, { name: config.name, rfc: config.rfc, fiscalAddress: config.fiscalAddress, phone: config.phone, contactEmail: config.contactEmail, logoUrl: config.logoUrl })
  Object.assign(location, { address: config.address || '', city: config.city || '', state: config.state || '', located: !!config.located })
  Object.assign(bank, { showBankDetails: config.showBankDetails, bankName: config.bankName, bankBeneficiary: config.bankBeneficiary, bankClabe: config.bankClabe, bankAccountNumber: config.bankAccountNumber, bankReferenceNote: config.bankReferenceNote })
  Object.assign(physical, { totalAreaM2: config.totalAreaM2, plotsCount: config.plotsCount })
  zones.value = config.zones
}
onMounted(load)

const showEmptyBanner = computed(() => !identity.name && !identity.rfc && !bank.bankClabe)

function onChange(key) {
  dirty[key] = true
  emit('dirty-change', Object.values(dirty).some(Boolean))
}

function showToast(msg) {
  toastMessage.value = msg
  setTimeout(() => (toastMessage.value = null), 3000)
}

async function onSaveIdentity() {
  await saveIdentity(props.parkId, { name: identity.name, rfc: identity.rfc, fiscalAddress: identity.fiscalAddress, phone: identity.phone, contactEmail: identity.contactEmail })
  dirty.parque = false
  emit('dirty-change', Object.values(dirty).some(Boolean))
  showToast('Datos de identidad actualizados')
}

async function onSaveLocation() {
  const res = await saveLocation(props.parkId, { address: location.address, city: location.city, state: location.state })
  location.located = !!res.located
  dirty.ubicacion = false
  emit('dirty-change', Object.values(dirty).some(Boolean))
  showToast(res.located || !location.city ? 'Ubicación actualizada' : 'Ubicación guardada, pero no encontramos la ciudad')
}

async function onSaveBank() {
  await saveBankDetails(props.parkId, { showBankDetails: bank.showBankDetails, bankName: bank.bankName, bankBeneficiary: bank.bankBeneficiary, bankClabe: bank.bankClabe, bankAccountNumber: bank.bankAccountNumber, bankReferenceNote: bank.bankReferenceNote })
  dirty['cobranza-bank'] = false
  emit('dirty-change', Object.values(dirty).some(Boolean))
  showToast('Datos bancarios actualizados')
}

async function onSavePhysical() {
  await savePhysical(props.parkId, { totalAreaM2: physical.totalAreaM2 })
  dirty.fisico = false
  emit('dirty-change', Object.values(dirty).some(Boolean))
  showToast('Datos físicos actualizados')
}

// --- Validaciones ---
const rfcValid = computed(() => /^[A-ZÑ&]{3,4}\d{6}[A-Z0-9]{3}$/.test((identity.rfc || '').toUpperCase()))
const emailValid = computed(() => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(identity.contactEmail || ''))
const clabeValid = computed(() => /^\d{18}$/.test(bank.bankClabe || ''))
const formattedClabe = computed(() => (bank.bankClabe || '').replace(/(\d{3})(?=\d)/g, '$1 '))

// --- Logo ---
const logoInputEl = ref(null)
const logoSrc = computed(() => (identity.logoUrl ? `${API_ORIGIN}/uploads/${identity.logoUrl}` : ''))

async function onLogoSelected(e) {
  const file = e.target.files[0]
  if (!file) return
  const updated = await uploadLogo(props.parkId, file)
  identity.logoUrl = updated.logoUrl
  e.target.value = ''
}

async function onDeleteLogo() {
  if (!confirm('¿Eliminar el logo? Los documentos generados después no tendrán logo.')) return
  await deleteLogo(props.parkId)
  identity.logoUrl = null
}

// --- Zonas ---
const editingZoneId = ref(null)
const editingZoneName = ref('')

function startRename(z) {
  editingZoneId.value = z.id
  editingZoneName.value = z.name
}

async function confirmRename(zoneId) {
  await renameZone(props.parkId, zoneId, editingZoneName.value)
  const z = zones.value.find((x) => x.id === zoneId)
  if (z) z.name = editingZoneName.value
  editingZoneId.value = null
}

async function onAddZone() {
  const zone = await addZone(props.parkId, 'Nueva zona')
  zones.value.push(zone)
  startRename(zone)
}

async function onDeleteZone(zoneId) {
  if (!confirm('¿Eliminar esta zona? Las naves asociadas quedarán sin zona asignada.')) return
  await deleteZone(props.parkId, zoneId)
  zones.value = zones.value.filter((z) => z.id !== zoneId)
}
</script>

<style scoped>
.cps { display: flex; flex-direction: column; gap: 1.5rem; max-width: 640px; }

.cps-banner { display: flex; align-items: center; gap: 0.5rem; height: 56px; background: #eff6ff; border: 1px solid #bfdbfe; border-radius: 8px; padding: 0 1rem; font-family: var(--font-body); font-size: 12px; color: #1d4ed8; }

.cps-group { display: flex; flex-direction: column; gap: 0.4rem; padding-bottom: 1.5rem; border-bottom: 1px solid #f1f5f9; }
.cps-group-title { font-family: var(--font-body); font-size: 12px; font-weight: 500; text-transform: uppercase; letter-spacing: 0.8px; color: #64748b; margin-bottom: 0.4rem; }

.cps-label { font-family: var(--font-body); font-size: 10px; text-transform: uppercase; color: #94a3b8; margin-top: 0.4rem; }
.cps-input, .cps-textarea { font-family: var(--font-body); font-size: 13px; color: #1e3a5f; border: 1px solid #e2e8f0; border-radius: 6px; padding: 0.5rem 0.6rem; box-sizing: border-box; width: 100%; }
.cps-input--error { border-color: #e84545; }
.cps-error { font-family: var(--font-body); font-size: 11px; color: #dc2626; }
.cps-warn { font-family: var(--font-body); font-size: 11px; color: #d97706; }
.cps-textarea { resize: vertical; }
.cps-row-2col { display: grid; grid-template-columns: 1fr 1fr; gap: 1rem; }
.cps-hint { font-family: var(--font-body); font-size: 10px; color: #94a3b8; }
.cps-hint-blue { font-family: var(--font-body); font-size: 10px; color: #3b82f6; }

.cps-logo-dropzone { height: 120px; border: 1px dashed #cbd5e1; background: #f8f9fc; border-radius: 8px; display: flex; flex-direction: column; align-items: center; justify-content: center; gap: 0.3rem; cursor: pointer; color: #94a3b8; font-family: var(--font-body); font-size: 12px; }
.cps-logo-dropzone .mdi { font-size: 32px; }
.cps-logo-preview-wrap { display: flex; align-items: center; gap: 1rem; }
.cps-logo-preview { width: 80px; height: 80px; border-radius: 8px; object-fit: contain; background: #f8f9fc; border: 1px solid #e2e8f0; }
.cps-logo-actions { display: flex; flex-direction: column; gap: 0.3rem; }
.cps-link-btn { background: none; border: none; color: #3b82f6; font-family: var(--font-body); font-size: 12px; cursor: pointer; text-align: left; padding: 0; }
.cps-link-btn--red { color: #dc2626; }

.cps-toggle-row { display: flex; align-items: center; gap: 0.4rem; font-family: var(--font-body); font-size: 13px; color: #1e3a5f; }
.cps-bank-fields { display: flex; flex-direction: column; gap: 0.2rem; transition: opacity 150ms ease; }
.cps-bank-fields--disabled { opacity: 0.5; pointer-events: none; }

.cps-bank-preview { background: #f8f9fc; border: 1px solid #e2e8f0; border-radius: 6px; min-height: 80px; padding: 0.6rem; font-family: var(--font-body); font-size: 12px; color: #1e3a5f; display: flex; flex-direction: column; justify-content: center; gap: 0.2rem; }
.cps-bank-preview-clabe { font-family: var(--font-mono); font-size: 13px; }
.cps-bank-preview-note { font-size: 10px; color: #94a3b8; }

.cps-input-suffix-wrap { display: flex; align-items: center; gap: 0.4rem; }
.cps-input-suffix-wrap .cps-input { width: 140px; }
.cps-input-suffix { font-family: var(--font-body); font-size: 13px; color: #94a3b8; }
.cps-readonly-value { font-family: var(--font-body); font-size: 13px; color: #64748b; background: #f8f9fc; border-radius: 6px; padding: 0.5rem 0.6rem; }

.cps-zones { display: flex; flex-direction: column; gap: 0.2rem; }
.cps-zone-row { display: flex; align-items: center; height: 36px; gap: 0.5rem; }
.cps-zone-name { flex: 1; font-family: var(--font-body); font-size: 13px; color: #1e3a5f; }
.cps-zone-input { flex: 1; padding: 0.3rem 0.5rem; }
.cps-zone-actions { display: flex; gap: 0.3rem; }
.cps-icon-btn { background: none; border: none; color: #94a3b8; cursor: pointer; display: flex; }

.cps-save-btn { align-self: flex-start; margin-top: 0.6rem; background: #3b82f6; color: #ffffff; border: none; border-radius: 6px; padding: 0.5rem 1rem; font-family: var(--font-body); font-size: 13px; font-weight: 500; cursor: pointer; }
.cps-save-btn:disabled { opacity: 0.4; cursor: default; }

.cps-toast { position: fixed; top: 1rem; right: 1rem; display: flex; align-items: center; gap: 0.4rem; background: #1e3a5f; color: #ffffff; padding: 0.6rem 1rem; border-radius: 8px; font-family: var(--font-body); font-size: 13px; box-shadow: 0 8px 24px rgba(0,0,0,0.2); z-index: 3000; }
.cps-toast .mdi { color: #10b981; }
.cps-toast-fade-enter-active, .cps-toast-fade-leave-active { transition: opacity 300ms ease; }
.cps-toast-fade-enter-from, .cps-toast-fade-leave-to { opacity: 0; }
</style>